import { useRef } from 'react';

import { createPortal } from 'react-dom';

import cn from 'classnames';

import { IconComponent } from 'components/Icon/IconComponent';
import { IconButton } from 'components/IconButton/IconButton';
import StatusPill from 'components/Pills/StatusPills';

import { useClickOutside } from '../../hooks/click-outside';
import styles from './AnimatedModal.module.scss';

export const AnimatedModal = ({
  visible,
  children,
  className,
  title,
  description,
  icon,
  noClose = false,
  customActions = [],
  headerActions,
  onClose = () => {},
  status,
  xSmall = false,
  small = false,
  large = false,
  noPadding = false,
  fullscreen = false,
  actionButtons,
  oldIconLibrary = true,
  hideHeader = false,
  noHeaderBorder = false,
  iconType = 'default',
  closeIconDataTestId,
  dataTestId,
}) => {
  const wrapperRef = useRef(null);

  useClickOutside(wrapperRef, () => {
    if (!noClose) onClose();
  });

  return createPortal(
    <div data-click-outside-ignore className={cn(styles.root, { [styles.rootVisible]: visible })}>
      <div
        className={cn(styles.contentWrapper, noPadding && '!p-0', {
          [styles.contentWrapperSmall]: small,
          [styles.contentWrapperXSmall]: xSmall,
          [styles.contentWrapperLarge]: large,
          [styles.contentWrapperFullscreen]: fullscreen,
        })}
        data-testid={dataTestId}
        ref={wrapperRef}>
        {!hideHeader && (
          <div
            className={cn(
              styles.header,
              'p-[16px]',
              'd-flex',
              'justify-content-between',
              'align-items-start',
              !noHeaderBorder && 'border-b',
              'border-neutral-4',
            )}>
            <div className={cn(styles.headerContent, 'flex', 'items-center', 'mr-16')}>
              {icon && (
                <div className="u-margin-right">
                  <IconComponent icon={icon} oldIconLibrary={oldIconLibrary} type={iconType} />
                </div>
              )}
              <div className="d-flex flex-column align-items-start">
                <div className={cn('d-flex align-items-center', description ? 'mt-0' : 'mt-2')}>
                  {title && (
                    <span className="text-neutral-7 font-medium text-4xl capitalize display-font">
                      {title}
                    </span>
                  )}
                  {status && (
                    <StatusPill
                      text={status.text}
                      status={status.status}
                      className={styles.status}
                    />
                  )}
                </div>
                {description && (
                  <p
                    className="u-font-weight-400 secondary-text"
                    dangerouslySetInnerHTML={{ __html: description }}
                  />
                )}
              </div>
            </div>
            <div className="d-flex align-items-center u-gap">
              {headerActions}
              {(customActions || []).map((action, index) => {
                return (
                  <IconButton
                    key={action.text + index}
                    icon={action.icon}
                    onClick={action.onClick}
                    tip={action.text}
                    prefix="hugeicons"
                  />
                );
              })}
              {!noClose && (
                <IconButton
                  icon="x"
                  onClick={onClose}
                  tip="Close"
                  placement="bottom"
                  dataTestId={closeIconDataTestId}
                />
              )}
            </div>
          </div>
        )}
        <div
          className={cn(
            noPadding ? 'p-0' : 'p-[16px]',
            fullscreen && 'flex-1 overflow-y-auto min-h-0',
            { [className]: className },
          )}>
          {children}
        </div>
        {actionButtons && <div>{actionButtons}</div>}
      </div>
    </div>,
    document.body,
  );
};
